import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";

import CreateCampaignModal from "common/components/CreateCampaignModal";
import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import Loader from "common/components/Loader";
import useWorkspaces from "common/datahooks/useWorkspaces";
import {
  SelectedWorkspaceProvider,
  useSelectedWorkspaceContext,
} from "common/helpers/SelectedWorkspaceContext";
import { UnreadMessagesWebsocketProvider } from "common/helpers/UnreadMessagesWebsocketContext";
import useAppStore from "common/hooks/useAppStore";
import useGTM from "common/hooks/useGTM";
import useNavItems from "common/hooks/useNavItems";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import useWorkspaceChangeUpdates from "common/hooks/useWorkspaceChangeUpdates";
import DesktopLayout from "common/layouts/WorkspaceLayout/DesktopLayout";
import MobileLayout from "common/layouts/WorkspaceLayout/MobileLayout";

import { workspaceRoutes } from "../../../pages/routes";
import { ContentContainer } from ".";
import AppSumoDialog from "./AppSumoDialog";
import WebPushPrompt from "./WebPushPrompt";

import NotFoundImage from "assets/images/404.png";

function Content({
  isLoading,
  errorComponent,
}: {
  isLoading: boolean;
  errorComponent: JSX.Element;
}) {
  useWorkspaceChangeUpdates();
  const { workspaceId } = useParams();
  const { sendEvent } = useGTM();
  const isDesktop = useTwBreakpoint("lg");
  const location = useLocation();
  const workspace = useSelectedWorkspaceContext();
  const navItems = useNavItems();
  const isCampaignModalOpen = useAppStore((state) => state.isCampaignModalOpen);
  const setIsCampaignModalOpen = useAppStore(
    (state) => state.setIsCampaignModalOpen,
  );

  useEffect(() => {
    window.scrollTo({ top: 0 });

    const pagePath = workspace
      ? location.pathname.replace(`/workspaces/${workspace.id}`, "")
      : location.pathname;

    if (pagePath) {
      sendEvent("virtualPageView", () => ({
        pageUrl: window.location.href,
        pagePath,
      }));
    }
  }, [location]);

  let content = <Outlet />;

  const currentRoute = workspaceRoutes.find(({ path }) =>
    location.pathname.includes(path.replace("/*", "").split("/")[0]),
  );

  if (errorComponent) {
    content = <ContentContainer>{errorComponent}</ContentContainer>;
  } else if (isLoading) {
    content = (
      <ContentContainer>
        <Loader />
      </ContentContainer>
    );
  } else if (
    currentRoute.visibleForPermissions &&
    !currentRoute.visibleForPermissions.some((permission) =>
      workspace.permissions.includes(permission),
    )
  ) {
    content = (
      <ContentContainer>
        <EmptyPlaceholder
          title="Page not found"
          subtitle="The page you are looking for is not available"
          imageSrc={NotFoundImage}
        />
      </ContentContainer>
    );
  }

  const isSettingsPage = location.pathname.includes("settings");

  let isLocked = false;
  if (!isSettingsPage && workspace) {
    const {
      subscription,
      rental_subscription: rentedSubscription,
      lifetime_license_count: lifetimeLicenseCount,
    } = workspace;

    isLocked =
      lifetimeLicenseCount === 0 &&
      (!subscription || subscription.status === "cancelled") &&
      (!rentedSubscription || rentedSubscription.status === "cancelled");
  }

  const LayoutComponent = isDesktop ? DesktopLayout : MobileLayout;

  return (
    <>
      <LayoutComponent
        key={workspaceId}
        navItems={navItems}
        workspace={workspace}
        isLocked={isLocked}
      >
        {content}
      </LayoutComponent>

      <WebPushPrompt />

      {workspace && (
        <>
          <CreateCampaignModal
            isOpen={isCampaignModalOpen}
            onClose={() => setIsCampaignModalOpen(false)}
          />
          <AppSumoDialog />
        </>
      )}
    </>
  );
}

export default function Layout() {
  const { workspaceId } = useParams();
  const { workspaces } = useWorkspaces();
  const location = useLocation();

  const selectedWorkspace = workspaces.find(({ id }) => id === workspaceId);

  // Validate the workspaceId in the url
  if (!workspaceId || !selectedWorkspace) {
    return <Navigate to={`/${workspaces[0].id}`} replace />;
  }

  // Check if the current location matches the base workspace URL and redirect to "/home"
  if (location.pathname === `/workspaces/${workspaceId}`) {
    return (
      <Navigate
        to={`/workspaces/${workspaceId}/${workspaceRoutes[0].path}`}
        replace
      />
    );
  }

  return (
    <SelectedWorkspaceProvider workspaceId={workspaceId}>
      {(props) => (
        <UnreadMessagesWebsocketProvider>
          <Content {...props} />
        </UnreadMessagesWebsocketProvider>
      )}
    </SelectedWorkspaceProvider>
  );
}
